package br.com.vvrefeicoes.portal.feature.auth.forgotPassword

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsFocusedAsState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing

private const val EMPTY_CELL = "\u200B"

@Composable
fun OtpCodeInput(
    value: List<Char?>,
    onValueChange: (List<Char?>) -> Unit,
    modifier: Modifier = Modifier,
    length: Int = 6,
    enabled: Boolean = true,
) {
    val focusRequesters = remember { List(length) { FocusRequester() } }
    val interactionSources = remember { List(length) { MutableInteractionSource() } }
    val focusManager = LocalFocusManager.current

    fun focusAt(index: Int) {
        if (index in 0 until length) {
            focusRequesters[index].requestFocus()
        } else {
            focusManager.clearFocus()
        }
    }

    fun handleInput(
        index: Int,
        raw: String,
    ) {
        val digits = raw.filter(Char::isDigit)
        val previous = value.getOrNull(index)

        if (digits.isEmpty()) {
            if (previous != null) {
                val updated = value.toMutableList()
                updated[index] = null
                onValueChange(updated)
            } else if (index > 0) {
                val updated = value.toMutableList()
                updated[index - 1] = null
                onValueChange(updated)
                focusAt(index - 1)
            }
            return
        }

        if (previous != null) {
            val replacement = digits.lastOrNull { it != previous } ?: previous
            val updated = value.toMutableList()
            updated[index] = replacement
            onValueChange(updated)
            focusAt(index + 1)
            return
        }

        val updated = value.toMutableList()
        var position = index
        for (digit in digits) {
            if (position >= length) break
            updated[position] = digit
            position++
        }
        onValueChange(updated)
        focusAt(position)
    }

    Row(
        modifier = modifier,
        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
    ) {
        repeat(length) { index ->
            OtpCell(
                char = value.getOrNull(index),
                enabled = enabled,
                focusRequester = focusRequesters[index],
                interactionSource = interactionSources[index],
                onValueChange = { raw -> handleInput(index, raw) },
            )
        }
    }
}

@Composable
private fun OtpCell(
    char: Char?,
    enabled: Boolean,
    focusRequester: FocusRequester,
    interactionSource: MutableInteractionSource,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
) {
    val focused by interactionSource.collectIsFocusedAsState()
    val borderColor =
        if (focused) {
            MaterialTheme.colorScheme.primary
        } else {
            MaterialTheme.colorScheme.outlineVariant
        }
    val shape = AppShapes.medium
    val text = char?.toString() ?: EMPTY_CELL
    val fieldValue =
        remember(text, focused) {
            TextFieldValue(text = text, selection = TextRange(text.length))
        }

    Box(
        modifier =
            modifier
                .width(48.dp)
                .height(56.dp)
                .background(MaterialTheme.colorScheme.surface, shape)
                .border(1.dp, borderColor, shape),
        contentAlignment = Alignment.Center,
    ) {
        BasicTextField(
            value = fieldValue,
            onValueChange = { new -> onValueChange(new.text) },
            modifier =
                Modifier
                    .fillMaxSize()
                    .focusRequester(focusRequester),
            enabled = enabled,
            singleLine = true,
            interactionSource = interactionSource,
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.NumberPassword),
            textStyle =
                TextStyle(
                    textAlign = TextAlign.Center,
                    color = MaterialTheme.colorScheme.onSurface,
                ),
            cursorBrush = SolidColor(MaterialTheme.colorScheme.primary),
            decorationBox = { innerTextField ->
                Box(
                    modifier = Modifier.fillMaxSize(),
                    contentAlignment = Alignment.Center,
                ) {
                    innerTextField()
                }
            },
        )
    }
}
